/**
 * @file hooks/useCrops.ts
 * @description 可驗證作物字典查詢與寫入 hook（管理頁 + 未來業務下拉共用）。
 *
 * 兩種查詢場景：
 *   1. 管理頁列表：用 useCropsList，支援主類別 Tab 切換、次類別過濾、搜尋、含已軟刪切換
 *   2. 下拉選單：用 useCropOptions，pageSize=500 一次拉回，5 分鐘 staleTime
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateCropInput,
  CropDto,
  CropMainCategory,
  CropScheme,
  UpdateCropInput,
} from '@zhcb/shared';
import { apiClient } from '@/lib/api-client';

/** 作物 DTO 類型別名，對外暴露供頁面使用 */
export type Crop = CropDto;

/** 分頁回應通用結構 */
interface PagedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/** useCropsList 的查詢選項 */
interface ListOpts {
  page?: number;
  pageSize?: number;
  search?: string;
  /** 驗證方案過濾（TOP/TAP，序230） */
  scheme?: CropScheme;
  /** 主要類別過濾（對應管理頁 Tab） */
  mainCategory?: CropMainCategory;
  /** 次要類別過濾 */
  subCategory?: string;
  activeOnly?: boolean;
  includeDeleted?: boolean;
  /** 控制 query 是否啟用，預設 true */
  enabled?: boolean;
}

/** QueryKey 根節點，所有衍生 key 皆以此為前綴，方便批次失效 */
const KEY = ['settings', 'crops'] as const;

/**
 * 管理頁分頁列表 hook。
 * 支援主類別、次類別、搜尋關鍵字、含已軟刪等過濾條件。
 */
export function useCropsList(opts: ListOpts = {}) {
  const {
    page = 1,
    pageSize = 50,
    search,
    scheme,
    mainCategory,
    subCategory,
    activeOnly,
    includeDeleted,
    enabled = true,
  } = opts;

  return useQuery({
    queryKey: [
      ...KEY,
      'list',
      { page, pageSize, search, scheme, mainCategory, subCategory, activeOnly, includeDeleted },
    ],
    queryFn: () => {
      const p = new URLSearchParams();
      p.set('page', String(page));
      p.set('pageSize', String(pageSize));
      if (search) p.set('search', search);
      if (scheme) p.set('scheme', scheme);
      if (mainCategory) p.set('mainCategory', mainCategory);
      if (subCategory) p.set('subCategory', subCategory);
      if (activeOnly) p.set('activeOnly', 'true');
      if (includeDeleted) p.set('includeDeleted', 'true');
      return apiClient.get<PagedResponse<Crop>>(`/api/settings/crops?${p.toString()}`);
    },
    enabled,
  });
}

/**
 * 下拉選單用 hook。
 * 一次拉回最多 500 筆啟用作物，快取 5 分鐘，避免重複請求。
 */
export function useCropOptions(opts: { scheme?: CropScheme; enabled?: boolean } = {}) {
  const { scheme, enabled = true } = opts;
  return useQuery({
    queryKey: [...KEY, 'options', scheme],
    queryFn: () => {
      const p = new URLSearchParams({ pageSize: '500', activeOnly: 'true' });
      if (scheme) p.set('scheme', scheme);
      return apiClient.get<PagedResponse<Crop>>(`/api/settings/crops?${p.toString()}`);
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** 匯出作物清單（序157：套用與列表相同的過濾條件，觸發瀏覽器下載） */
export function buildCropsExportUrl(
  opts: Pick<ListOpts, 'scheme' | 'mainCategory' | 'subCategory' | 'search' | 'activeOnly'> = {},
) {
  const p = new URLSearchParams();
  if (opts.scheme) p.set('scheme', opts.scheme);
  if (opts.mainCategory) p.set('mainCategory', opts.mainCategory);
  if (opts.subCategory) p.set('subCategory', opts.subCategory);
  if (opts.search) p.set('search', opts.search);
  if (opts.activeOnly) p.set('activeOnly', 'true');
  const qs = p.toString();
  return `/api/settings/crops/export.xlsx${qs ? `?${qs}` : ''}`;
}

/**
 * 新增作物 mutation。
 * 成功後自動失效 KEY 下所有快取（列表 + 下拉同步更新）。
 */
export function useCreateCrop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCropInput) =>
      apiClient.post<{ item: Crop }>('/api/settings/crops', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * 更新作物 mutation（PATCH 局部更新）。
 * 成功後自動失效 KEY 下所有快取。
 */
export function useUpdateCrop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateCropInput }) =>
      apiClient.patch<{ item: Crop }>(`/api/settings/crops/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * 軟刪除作物 mutation。
 * 成功後自動失效 KEY 下所有快取。
 */
export function useDeleteCrop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiClient.delete<{ ok: true }>(`/api/settings/crops/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
